import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { Card, CardContent } from "@shared/ui/card";
import { RadioGroup, RadioGroupItem } from "@shared/ui/radio-group";
import { Users, CircleDot, CheckCircle2 } from "lucide-react";

type SubAccountCardProps = {
  showAlert: boolean;
  masterAccount: {
    name: string;
    number: string;
    phone: string;
    address: string;
  };
  onOptionChange?: (value: "Master" | "New") => void;
  selectedOption?: "Master" | "New" | "Owner";
};

export const SubAccountCard = ({
  showAlert,
  masterAccount,
  onOptionChange,
  selectedOption,
}: SubAccountCardProps) => {
  const isTransferred = showAlert;

  const masterDetails = [
    { label: "MASTER ACCOUNT NAME", value: masterAccount.name },
    { label: "MASTER ACCOUNT NUMBER", value: masterAccount.number },
    { label: "MASTER ACCOUNT PHONE", value: masterAccount.phone },
    { label: "MASTER ACCOUNT ADDRESS", value: masterAccount.address },
  ];

  return (
    <Card className="rounded-xl border border-blue-100 bg-blue-50/40 overflow-hidden shadow-sm">
      <CardContent className="p-0">
        {/* Header */}
        <div className="flex items-center justify-between bg-blue-50 px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 p-2.5 rounded-full flex items-center justify-center">
              <Users className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <p className="font-semibold text-blue-700">
                {isTransferred ? "This is a Sub-Account" : "Sub-Account Detected"}
              </p>
              <p className="text-sm text-blue-600">
                {isTransferred
                  ? "Previously transferred to the master account"
                  : "This account is linked under a master account"}
              </p>
            </div>
          </div>
           <span className="text-xs font-semibold tracking-wider text-white bg-blue-600 px-3 py-1 rounded-full">
           SUB-ACCOUNT
          </span>
        </div>

        {/* Content */}
        <div className="p-5 space-y-4">
          {/* Banner */}
          <div
            className={`rounded-md px-4 py-3 text-sm ${
              isTransferred
                ? "bg-blue-50 text-blue-700 border-l-4 border-blue-400"
                : "bg-orange-50 text-orange-700 border-l-4 border-orange-400"
            }`}
          >
            {isTransferred
              ? "This account is a sub-account and was previously transferred to the master account holder."
              : "This account is currently a sub-account under a master account. Please choose how you would like to proceed with the transfer."}
          </div>

          {/* Master Details */}
          <div className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
            {masterDetails.map((item, index) => (
              <div key={index}>
                <ValueLabel value={item.value} label={item.label} />
              </div>
            ))}
          </div>

          {/* Conditional section */}
          {isTransferred ? (
            <div className="flex items-center gap-2 text-green-700 font-medium">
              <CheckCircle2 className="w-5 h-5" />
              Transferred to Master Account
            </div>
          ) : (
            <div>
              <p className="font-medium mb-3 flex items-center gap-2 text-gray-800">
                <CircleDot className="w-4 h-4 text-orange-500" />
                How would you like to proceed with this transfer?
              </p>

              <RadioGroup
                value={selectedOption}
                onValueChange={onOptionChange}
                className="grid grid-cols-2 gap-4"
              >
                {/* Option 1 */}
                <label
                  className={`border rounded-lg p-4 flex gap-3 cursor-pointer transition-colors ${
                    selectedOption === "Master"
                      ? "border-blue-500 bg-blue-50 ring-1 ring-blue-500"
                      : "border-gray-200 bg-white hover:border-gray-300"
                  }`}
                >
                  <RadioGroupItem value="Master" className="mt-0.5" />
                  <div>
                    <p className="font-semibold text-gray-900">
                      Transfer on Master Account
                    </p>
                    <p className="text-sm text-gray-500 mt-1 leading-relaxed">
                      Transfer this sub-account to the master account holder. The master account will assume all service responsibilities and billing for this connection.
                    </p>
                  </div>
                </label>

                {/* Option 2 */}
                <label
                  className={`border rounded-lg p-4 flex gap-3 cursor-pointer transition-colors ${
                    selectedOption === "New"
                      ? "border-blue-500 bg-blue-50 ring-1 ring-blue-500"
                      : "border-gray-200 bg-white hover:border-gray-300"
                  }`}
                >
                  <RadioGroupItem value="New" className="mt-0.5" />
                  <div>
                    <p className="font-semibold text-gray-900">
                      Transfer to New Consumer
                    </p>
                    <p className="text-sm text-gray-500 mt-1 leading-relaxed">
                      Transfer this sub-account to a completely new consumer. The account will be detached from the master account and assigned to a new individual.
                    </p>
                  </div>
                </label>
              </RadioGroup>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};